import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import LandingVideo from '@src/components/DisposalPage/LandingVideo'
import HexagonFeatures from '@src/components/DisposalPage/HexagonFeatures'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import solarClients from '@src/constants/solarClients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import solar from '../../json/solar-decommissioning-data.json'

const decommissioningServiceData = {
  name: 'Solar Project Decommissioning and Uninstallation',
  description:
    'Commercial and utility-scale solar decommissioning nationwide. Dimension handles uninstallation, PV module and battery removal, recycling and salvage recovery, site restoration, and full regulatory documentation.',
  serviceType: 'Solar Decommissioning',
  url: 'https://getdimension.com/solar-decommissioning',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Commercial Solar Decommissioning' },
    { name: 'Utility-Scale Solar Uninstallation' },
    { name: 'PV Module and Inverter Removal' },
    { name: 'Battery and Energy Storage Removal' },
    { name: 'Site Restoration and Compliance Reporting' },
  ],
}

const decommissioningFAQData = {
  questions: solar.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function SolarDecommissioning() {
  return (
    <>
      <Head>
        <title>Solar Panel Decommissioning Services | Dimension</title>
        <meta
          name="description"
          content="Commercial and utility-scale solar decommissioning nationwide. Dimension handles uninstallation, recycling and salvage, site restoration, and full compliance documentation."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/solar-decommissioning" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={decommissioningServiceData} />
        <SchemaMarkup type="FAQPage" data={decommissioningFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="solar"
          heading={solar.pitch.heading}
          material={solar.pitch.material}
          pitchText={solar.pitch.pitchText}
          ctaLink={solar.pitch.ctaLink}
          ctaText={solar.pitch.ctaText}
          ctaHeading={solar.pitch.ctaHeading}
          ctaSubtext={solar.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <LandingVideo service="solar-decommissioning" />
        <Clients clients={solarClients} />
        <HexagonFeatures />
        <FeatureList {...solar.solutions} />
        <FeatureList {...solar.benefits} />
        <CaseStudy {...solar.caseStudy} />
        <Approach {...solar.approach} />
        <FAQ {...solar.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Solar Panel Disposal & Recycling',
              href: '/solar-panel-disposal',
              description:
                'Recycling and disposal for old, damaged, and decommissioned panels, batteries, and inverters.',
            },
            {
              label: 'Sell Used Solar Panels',
              href: '/solar-panel-sell',
              description:
                'Recover value from functional panels and equipment removed during a repowering or upgrade.',
            },
            {
              label: 'E-Waste Management',
              href: '/ewaste',
              description:
                'Certified recycling for inverters, batteries, and the electronics in a solar installation.',
            },
            {
              label: 'B2B Solutions',
              href: '/b2b-solutions',
              description:
                'Managed waste and recycling programs for businesses running projects across many sites.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default SolarDecommissioning
